import { useMemo } from 'react';
import { X } from 'lucide-react';
import { RAMPA_DE_CARGA, barClass, barStyle, priorityColor } from './ganttTheme';
import { DEFAULT_HORAS } from './ganttExplica';

/**
 * La guia del Gantt, acoplada al costado y no en un modal.
 *
 * El patron de `HelpButton` —un modal con un muro de texto— no sirve para un grafico: tapa
 * justo lo que hay que mirar, y obliga a memorizar en vez de comparar. Aca la guia queda
 * abierta al lado, con **muestras dibujadas por el mismo codigo que dibuja las barras**
 * (`ganttTheme`), asi una leyenda no puede quedar desactualizada respecto del grafico. Una
 * leyenda que miente es peor que ninguna.
 *
 * Se abre sola la primera vez y no vuelve a abrirse: no hay tour, ni pasos, ni un aviso que
 * haya que cerrar antes de trabajar.
 */

function Muestra({ row, children }) {
  return (
    <div className="flex items-start gap-2.5">
      <span className="w-14 h-3 shrink-0 mt-0.5 relative">
        <span className={`absolute inset-0 rounded-sm ${barClass(row)} ${barStyle(row)}`} />
      </span>
      <span className="text-[10px] text-zinc-600 leading-snug">{children}</span>
    </div>
  );
}

function Seccion({ titulo, children }) {
  return (
    <div className="space-y-2">
      <h3 className="text-[9px] font-mono uppercase tracking-[0.14em] text-zinc-400">{titulo}</h3>
      {children}
    </div>
  );
}

export default function GanttGuia({ rows, meta, carga, onClose }) {
  const plan = meta?.plan_vs_compromiso;
  const cuenta = useMemo(() => {
    const tareas = (rows || []).filter((r) => r.row_type === 'task');
    return {
      tareas: tareas.length,
      deducidas: tareas.filter((r) => r.start_inferred || r.end_inferred).length,
      sinEstimar: tareas.filter((r) => !Number(r.estimate_hours)).length,
    };
  }, [rows]);

  return (
    <aside
      className="w-64 shrink-0 bg-white border border-brand-border rounded-sm flex flex-col overflow-hidden self-start max-h-[calc(100vh-9rem)] sticky top-0"
      data-testid="gantt-guia"
    >
      <div className="flex items-start justify-between gap-2 px-3 py-2 border-b border-brand-border bg-zinc-50">
        <div>
          <h2 className="text-xs font-bold text-[#09090B]">Como se lee</h2>
          <p className="text-[9px] text-zinc-500 mt-0.5">Se abre sola la primera vez.</p>
        </div>
        <button
          type="button"
          onClick={onClose}
          data-testid="gantt-guia-cerrar"
          className="text-zinc-400 hover:text-[#09090B] p-0.5"
          aria-label="Cerrar la guia"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>

      <div className="flex-1 overflow-auto px-3 py-3 space-y-4">
        <Seccion titulo="Cada fila">
          <Muestra row={{ row_type: 'ticket' }}>
            <b className="text-[#09090B]">Ticket.</b> Lo que suman sus tareas, de la primera a la ultima.
          </Muestra>
          <Muestra row={{ row_type: 'phase' }}>
            <b className="text-[#09090B]">Fase.</b> Lo que suman las tareas de esa fase.
          </Muestra>
          <Muestra row={{ row_type: 'task' }}>
            <b className="text-[#09090B]">Tarea.</b> La unica fila con fechas propias.
          </Muestra>
        </Seccion>

        <Seccion titulo="De donde sale cada fecha">
          <Muestra row={{ row_type: 'task' }}>
            <b className="text-[#09090B]">Confirmada.</b> La cargo una persona.
          </Muestra>
          <Muestra row={{ row_type: 'task', start_inferred: true }}>
            <b className="text-[#09090B]">Deducida.</b> La calculo el sistema. Nadie la miro.
          </Muestra>
          {cuenta.tareas > 0 && (
            <p className="text-[10px] text-zinc-600 leading-snug pt-0.5" data-testid="gantt-guia-cuenta">
              Ahora mismo <b className="font-mono text-[#09090B]">{cuenta.deducidas}</b> de{' '}
              <b className="font-mono text-[#09090B]">{cuenta.tareas}</b> tareas tienen fecha deducida
              {cuenta.sinEstimar > 0 && (
                <>
                  , y <b className="font-mono text-[#09090B]">{cuenta.sinEstimar}</b> no tienen esfuerzo
                  estimado: esas barras miden {DEFAULT_HORAS} h porque es el default, no porque haya {DEFAULT_HORAS} h de trabajo
                </>
              )}
              .
            </p>
          )}
          {/* **Lo que falta, dicho.** Habia dos recortes silenciosos —500 casos y 2000 filas— y
              `row_count` se calculaba despues del corte, asi que un Gantt incompleto se veia
              completo. Peor: una dependencia podia apuntar a una fila que ya no viajaba y la flecha
              desaparecia sin aviso. El tope se queda; lo que no puede quedarse es el silencio. */}
          {(meta?.rows_truncadas > 0 || meta?.tickets_leidos >= meta?.tickets_tope) && (
            <p className="text-[10px] text-amber-800 bg-amber-50 border border-amber-200 rounded-sm p-1.5 leading-snug"
               data-testid="gantt-guia-truncado">
              <b>Esto no es el plan completo.</b>{' '}
              {meta.rows_truncadas > 0
                ? `Quedaron ${meta.rows_truncadas} fila(s) afuera del tope de la pantalla`
                : `Se leyeron los ${meta.tickets_tope} casos mas recientes`}
              . Las dependencias que apuntan a una fila que no entro **no se dibujan**. Acota por
              cápsula, gerencia o fechas para ver todo.
            </p>
          )}
          <p className="text-[10px] text-zinc-500 leading-snug">
            Pasa el mouse por cualquier barra y te dice de donde salio esa fecha.
          </p>
        </Seccion>

        <Seccion titulo="Que dice el color">
          <p className="text-[10px] text-zinc-600 leading-snug">
            El color de la barra es <b className="text-[#09090B]">el tipo de fila</b>, no la prioridad. El
            numero adentro es el avance.
          </p>
          <div className="flex flex-col gap-1 pt-0.5">
            {[['critical', 'Critica'], ['high', 'Alta'], ['medium', 'Media'], ['low', 'Baja']].map(([id, n]) => (
              <div key={id} className={`flex items-center gap-2 pl-1.5 ${priorityColor(id)}`}>
                <span className="text-[10px] text-zinc-600">{n}</span>
              </div>
            ))}
          </div>
          <p className="text-[10px] text-zinc-500 leading-snug">
            La prioridad es la marca del borde izquierdo, en la columna de nombres.
          </p>
        </Seccion>

        {/* La guia que no puede quedar vieja: cuenta las filas que tiene a mano. */}
        <Seccion titulo="Lo hecho y lo gastado">
          <p className="text-[10px] text-zinc-600 leading-snug">
            El <b className="text-[#09090B]">relleno</b> es cuanto esta hecho, declarado por quien
            trabaja la tarea. La <b className="text-[#09090B]">linea fina al pie</b> es cuantas horas
            se gastaron contra las estimadas. Son dos cosas: una tarea puede haber gastado todo su
            presupuesto y estar a medias.
          </p>
          <p className="text-[10px] text-zinc-600 leading-snug">
            Cuando esa linea se pone <b className="text-brand-danger">roja</b>, se gasto mas de lo
            que se avanzo. Es la alerta que llega a tiempo: la fecha vencida llega tarde.
          </p>
          {(() => {
            const tareas = (rows || []).filter((r) => r.row_type === 'task');
            const sinDeclarar = tareas.filter((r) => r.progress == null).length;
            const deMas = tareas.filter((r) => r.consumio_mas_de_lo_que_avanzo).length;
            if (!tareas.length) return null;
            return (
              <p className="text-[10px] text-zinc-500 leading-snug">
                Ahora mismo <b className="font-mono text-[#09090B]">{deMas}</b> de{' '}
                <b className="font-mono text-[#09090B]">{tareas.length}</b> gastaron de mas, y en{' '}
                <b className="font-mono text-[#09090B]">{sinDeclarar}</b> nadie declaro cuanto esta
                hecho — esas barras salen sin relleno, que no es lo mismo que cero.
              </p>
            );
          })()}
        </Seccion>

        <Seccion titulo="Si el caso llega o no">
          <p className="text-[10px] text-zinc-600 leading-snug">
            La barra del ticket dice <b className="text-[#09090B]">cuando se va a hacer</b>; lo
            prometido es otra fecha. Si el plan termina despues, la fila lo marca.
          </p>
          <div className="flex items-center gap-2">
            <span className="text-[8px] uppercase tracking-wider px-1 py-px border rounded-sm text-brand-danger border-red-200 bg-red-50">
              +3 d
            </span>
            <span className="text-[10px] text-zinc-600 leading-snug">dias habiles tarde</span>
          </div>

          {/* Las verticales. Estaban dibujadas y no explicadas: se veian dos colores, en
              lugares distintos por caso, sin nada que dijera de que eran. */}
          <div className="pt-1 space-y-1.5">
            <div className="flex items-start gap-2.5">
              <span className="w-14 h-4 shrink-0 relative">
                <span className="absolute left-0 right-4 top-1 h-2 rounded-sm bg-[#E0E6F6]" />
                <span className="absolute top-0 bottom-0" style={{ left: 42, borderLeft: '1.5px dashed #09090B' }} />
              </span>
              <span className="text-[10px] text-zinc-600 leading-snug">
                <b className="text-[#09090B]">Vertical negra:</b> lo prometido. La barra termina
                antes, asi que entra.
              </span>
            </div>
            <div className="flex items-start gap-2.5">
              <span className="w-14 h-4 shrink-0 relative">
                <span className="absolute left-0 right-0 top-1 h-2 rounded-sm bg-[#E0E6F6]" />
                <span className="absolute top-0 bottom-0" style={{ left: 30, borderLeft: '1.5px dashed #FF2A00' }} />
              </span>
              <span className="text-[10px] text-zinc-600 leading-snug">
                <b className="text-[#09090B]">Vertical roja:</b> el plan la cruza. Se sigue
                trabajando despues de la fecha prometida.
              </span>
            </div>
            <div className="flex items-start gap-2.5">
              <span className="w-14 h-4 shrink-0 relative">
                <span className="absolute top-0 bottom-0" style={{ left: 28, borderLeft: '1px solid #002FA7', opacity: 0.6 }} />
              </span>
              <span className="text-[10px] text-zinc-600 leading-snug">
                <b className="text-[#09090B]">Linea azul continua:</b> hoy. Cruza todo el
                tablero y lleva su etiqueta arriba.
              </span>
            </div>
          </div>
          <p className="text-[10px] text-zinc-500 leading-snug">
            <b className="text-[#09090B]">Hay una por caso, y solo sobre sus propias filas.</b>
            Cada ticket promete su fecha, asi que cada uno tiene la suya, en el dia que prometio
            — por eso estan en lugares distintos. Una linea de punta a punta obligaria a seguirla
            con el dedo para saber de que caso es.
          </p>
          {plan && (
            <p className="text-[10px] text-zinc-600 leading-snug" data-testid="gantt-guia-plan">
              En este tablero <b className="font-mono text-[#09090B]">{plan.no_entra}</b> de{' '}
              <b className="font-mono text-[#09090B]">{plan.total}</b> casos no entran
              {plan.dias_de_atraso > 0 && (
                <>, y suman <b className="font-mono text-[#09090B]">{plan.dias_de_atraso}</b> dias
                habiles que hay que sacar de algun lado</>
              )}
              .
            </p>
          )}
          <p className="text-[10px] text-zinc-500 leading-snug">
            Un caso <b className="text-[#09090B]">sin cierre comprometido</b> no esta mal: hay
            pedidos que prometen atencion y no fecha, y se estiman despues de analizarlos.
          </p>
        </Seccion>

        <Seccion titulo="La franja de abajo">
          <p className="text-[10px] text-zinc-600 leading-snug">
            Cuanto trabajo tiene encima cada persona, <b className="text-[#09090B]">dia por
            dia</b> y sobre el mismo eje. Tres barras paralelas de la misma persona se ven igual
            de prolijas que tres de tres personas — hasta que se mira esto.
          </p>
          <div className="flex items-end gap-1 h-8 relative pt-1">
            <span className="absolute left-0 right-16 border-t border-dashed border-zinc-300" style={{ bottom: 14 }} />
            {[30, 60, 100].map((pct, i) => (
              <span key={pct} className="w-4 inline-block" style={{ height: 4 + i * 5, background: RAMPA_DE_CARGA[i] }} />
            ))}
            <span
              className="w-4 inline-block"
              style={{
                height: 20, background: RAMPA_DE_CARGA[3], borderTop: '2px solid #FFB800',
                backgroundImage: 'repeating-linear-gradient(135deg,rgba(255,255,255,.6) 0 2px,transparent 2px 5px)',
              }}
            />
            <span className="text-[9px] text-zinc-500 ml-1 mb-0.5">← capacidad</span>
          </div>
          <p className="text-[10px] text-zinc-500 leading-snug">
            Un solo tono. Pasarse no se marca solo con color: se marca con
            <b className="text-[#09090B]"> textura</b> y cruzando la linea, que se ve igual en
            escala de grises.
          </p>
          {(carga?.conflictos || []).length > 0 && (
            <p className="text-[10px] text-zinc-600 leading-snug" data-testid="gantt-guia-carga">
              Ahora mismo hay <b className="font-mono text-[#09090B]">{carga.conflictos.length}</b>{' '}
              conflicto(s) de carga. La franja de abajo del todo los lista y no se va sola: un
              conflicto no es un aviso que pasa, es un estado que sigue estando manana.
            </p>
          )}
        </Seccion>

        <Seccion titulo="Que podes hacer">
          <ul className="text-[10px] text-zinc-600 leading-snug space-y-1 list-none">
            <li>Clic en una fila abre el ticket o la tarea.</li>
            <li>
              <b className="text-[#09090B]">⌘/Ctrl + rueda</b> acerca y aleja el eje, y el dia
              que tenes bajo el puntero se queda donde esta. Los botones de arriba son atajos.
            </li>
            <li>Los filtros de arriba recortan que tickets entran.</li>
            <li>La flecha entre dos barras es una dependencia: la de la derecha espera a la de la izquierda.</li>
          </ul>
        </Seccion>

        <Seccion titulo="Los tres gestos de una barra">
          <ul className="text-[10px] text-zinc-600 leading-snug space-y-1 list-none">
            <li><b className="text-[#09090B]">El cuerpo mueve la fecha.</b> El mismo trabajo, en otro momento.</li>
            <li>
              <b className="text-[#09090B]">El borde cambia la dedicacion</b>, no la estimacion.
              El esfuerzo es un hecho —cuanto trabajo hay—; la duracion sale de a cuanto por dia
              se lo atiende, y eso es lo que se decide al estirar.
            </li>
            <li>
              <b className="text-[#09090B]">El circulo del extremo vincula</b>: tocalo y despues
              toca la tarea que espera a esta. Mientras dure, una franja arriba lo dice y
              <b className="text-[#09090B]"> Esc</b> cancela.
            </li>
          </ul>
          <p className="text-[10px] text-zinc-600 leading-snug border-t border-zinc-100 pt-1.5">
            <b className="text-[#09090B]">Soltar no confirma: simula.</b> Aparece que se corre,
            a que compromiso le pega y quien queda sobreasignado. Nada se escribe hasta que
            aprietes Confirmar.
          </p>
        </Seccion>

        <Seccion titulo="Lo que todavia no muestra">
          <p className="text-[10px] text-zinc-600 leading-snug">
            Los conflictos se ven pero <b className="text-[#09090B]">no traen su salida</b>:
            repartir la carga, posponer o reasignar todavia se hacen a mano desde la tarea.
          </p>
        </Seccion>
      </div>
    </aside>
  );
}
